import React, { createContext, useContext, useState, useEffect } from 'react';

interface LocationData {
  latitude: number;
  longitude: number;
  country_code?: string; // Two-letter country code
  city?: string;
  region?: string;
  postal_code?: string;
  source: 'ip' | 'browser';
}

interface LocationContextType {
  location: LocationData | null;
  isLoading: boolean;
  error: string | null;
  hasPermission: boolean;
}

const LocationContext = createContext<LocationContextType | null>(null);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location, setLocation] = useState<LocationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState(true); // IP location doesn't need permission

  const reverseGeocode = async (latitude: number, longitude: number): Promise<Partial<LocationData> | null> => {
    try {
      const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
      if (!response.ok) {
        throw new Error('Failed to reverse geocode');
      }
      
      const data = await response.json();
      
      return {
        country_code: data.countryCode,
        city: data.city,
        region: data.principalSubdivision,
        postal_code: data.postcode
      };
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return null;
    }
  };

  const getBrowserLocation = (): Promise<LocationData | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Get address details from coordinates
          const addressData = await reverseGeocode(latitude, longitude);
          
          if (addressData) {
            resolve({
              latitude,
              longitude,
              country_code: addressData.country_code,
              city: addressData.city,
              region: addressData.region,
              postal_code: addressData.postal_code,
              source: 'browser' as const
            });
          } else {
            // Fallback to just coordinates if reverse geocoding fails
            resolve({
              latitude,
              longitude,
              source: 'browser' as const
            });
          }
        },
        (error) => {
          console.error('Browser geolocation failed:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  const getIPLocation = async (): Promise<LocationData | null> => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        throw new Error('Failed to fetch location data');
      }
      
      const data = await response.json();
      
      return {
        latitude: data.latitude,
        longitude: data.longitude,
        country_code: data.country_code,
        city: data.city,
        region: data.region,
        postal_code: data.postal,
        source: 'ip' as const
      };
    } catch (error) {
      console.error('IP location failed:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchLocation = async () => {
      setIsLoading(true);
      setError(null);

      // Try browser geolocation first
      let locationData = await getBrowserLocation();
      
      // Fallback to IP location if browser geolocation fails
      if (!locationData) {
        locationData = await getIPLocation();
      }
      
      if (locationData) {
        setLocation(locationData);
        console.log('Location obtained:', locationData);
      } else {
        setError('Unable to determine location');
      }
      
      setIsLoading(false);
    };

    fetchLocation();
  }, []);

  return (
    <LocationContext.Provider value={{ 
      location, 
      isLoading, 
      error, 
      hasPermission 
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}; 